import { LOCATION_CHANGE, LocationChangeAction } from 'react-router-redux';
import { AnyAction } from 'redux';
import { ActionsObservable, combineEpics, ofType, StateObservable } from 'redux-observable';
import { of } from 'rxjs';
import { ajax, AjaxError } from 'rxjs/ajax';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import produce from 'immer';
import {
  ERROR_UPDATE_DUMMY_DATA_VISIBILITY,
  ERROR_UPDATE_DUMMY_PAYOFF_VISIBILITY,
  UPDATE_DUMMY_DATA_VISIBILITY,
  UPDATE_DUMMY_PAYOFF_VISIBILITY,
  UPDATED_DUMMY_DATA_VISIBILITY,
  UPDATED_DUMMY_PAYOFF_VISIBILITY,
} from './actions';

import { AscendState } from '.';
import {
  ADD_LOCAL_STORAGE,
  FETCH_USER_INFO,
  FOUND_USER_INFO,
  ERROR_USER_INFO,
  CONTACT_SUBMIT,
  SUCCESS_CONTACT_SUBMIT,
  ERROR_CONTACT_SUBMIT,
  SUCCESS_SUBMIT_AGREEMENT,
} from './actions';
import auth from '../auth/auth';
import { CardInfo } from './billing';
import config from '../config';

type AscendReview = {
  comment: string;
  displayName: string;
  profilePhotoUrl: string;
  starRating: number;
};

// Reducer
type UserData = {
  guid: string;
  id: number;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  advisorChatPhotoUrl: string;
  advisorName: string;
  advisorReviews: AscendReview[];
  ascendFee: number;
  dateOfBirth: string;
  ssn: string;
  clientSignature: string;
  streetAddress: string;
  city: string;
  state: string;
  stateFull: string | null;
  zip: string;
  mxUserGuid: string;
  governmentId: string;
  contactFirstName: string;
  contactLastName: string;
  contactSSN: string;
  contactDateOfBirth: string;
  bankName: string;
  accountType: string;
  accountHolderName: string;
  accountHolderStreetAddress: string;
  accountHolderCity: string;
  accountHolderState: string;
  accountHolderZip: string;
  draftOption: string;
  draftAmount: string;
  initialDraftAmount: string;
  initialDraftDate: string;
  recurringDraftDate: string;
  dateSigned: string;
  clientInitials: string;
  routingNumber: string;
  accountNumber: string;
  totalDebt: number;
  estimatedEnrolledDebt: number;
  estimatedPayoff: number;
  freshStartFromCalc: boolean;
  currentPage: number;
  estimatedSettlePercent: number;
  submittedAgreement: boolean;
  showPortalCsa: boolean;
  card: CardInfo;
  csaUrl: string;
  signupMapping: string;
  showDummyData: boolean;
  showDummyDataPayoff: boolean;
  programLengthMonths: number;
  programLengthPayments: number;
  lastQuizUrl: string;
  lastResultsUrl: string;
};

export type UserState = {
  email: string;
  isAdmin: boolean;
  contactMessageSubmitted: boolean;
  data: UserData;
  loading: boolean;
  error: AjaxError;
};

const defaultState: UserState = {
  email: '',
  isAdmin: false,
  contactMessageSubmitted: false,
  data: {} as UserData,
  loading: false,
  error: {} as AjaxError,
};
export const userReducer = (state = defaultState, action: AnyAction) =>
  produce(state, (draftState) => {
    switch (action.type) {
      case ADD_LOCAL_STORAGE: {
        draftState.email = action.email;
        draftState.isAdmin = action.isAdmin;
        return;
      }
      case FETCH_USER_INFO: {
        draftState.email = action.email;
        draftState.loading = true;
        return;
      }
      case SUCCESS_SUBMIT_AGREEMENT: {
        if (action.submitted) {
          draftState.data.submittedAgreement = action.submitted;
        }
        return;
      }
      case SUCCESS_CONTACT_SUBMIT: {
        draftState.contactMessageSubmitted = true;
        return;
      }
      case FOUND_USER_INFO: {
        localStorage.setItem('email', action.data.email);

        draftState.data = action.data;
        draftState.loading = false;
        return;
      }
      case ERROR_USER_INFO: {
        draftState.error = action.error;
        return;
      }
      case UPDATED_DUMMY_DATA_VISIBILITY: {
        // Ensure the user data exists before trying to update it
        if (draftState.data) {
          draftState.data.showDummyData = false;
        }
        return;
      }
      case UPDATED_DUMMY_PAYOFF_VISIBILITY: {
        if (draftState.data) {
          draftState.data.showDummyDataPayoff = false;
        }
        return;
      }
    }
  });

// Selectors
export function getUserState(state: AscendState) {
  return state.user;
}

// Epics
const addLocalStorageToState$ = (
  action$: ActionsObservable<LocationChangeAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    withLatestFrom(state$),
    filter(([_, state]) => !state.user.email && auth.isAuthenticated()),
    map(() => ({
      type: ADD_LOCAL_STORAGE,
      email: localStorage.getItem('email'),
      isAdmin: localStorage.getItem('isAdmin') === 'true',
    }))
  );

const fetchUserInfo$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FETCH_USER_INFO),
    switchMap((action) => {
      const encodedEmail = encodeURIComponent(action.email);
      const url = `${config.apiUrl}/user?email=${encodedEmail}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest);
    }),
    map((data) => ({ type: FOUND_USER_INFO, data: data.response })),
    catchError((error: AjaxError) => of({ type: ERROR_USER_INFO, error }))
  );

const shouldFetchInfo$ = (
  action$: ActionsObservable<LocationChangeAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    withLatestFrom(state$),
    filter(([_, state]) => auth.isAuthenticated() && !state.user.data.id && !!state.user.email),
    map(([_, state]) => ({ type: FETCH_USER_INFO, email: state.user.email }))
  );

const contact$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(CONTACT_SUBMIT),
    switchMap((action) => {
      const url = `${config.apiUrl}/contact`;
      const body = { message: action.message };
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, body, headers, method: 'POST', withCredentials: true };
      return ajax(ajaxRequest);
    }),
    map(() => ({ type: SUCCESS_CONTACT_SUBMIT })),
    catchError((error: AjaxError) => of({ type: ERROR_CONTACT_SUBMIT, error }))
  );

// Epic to update the dummy data flag
const updateDummyDataVisibility$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(UPDATE_DUMMY_DATA_VISIBILITY),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { userId } = action.payload;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      // The API endpoint is the same as for fetching/updating user info.
      return ajax
        .put(`${config.apiUrl}/updateShowDummyData`, { userId, show_dummy_data: false }, headers)
        .pipe(
          map((response) => ({
            type: UPDATED_DUMMY_DATA_VISIBILITY,
            payload: response.response,
          })),
          catchError((error: AjaxError) => of({ type: ERROR_UPDATE_DUMMY_DATA_VISIBILITY, error }))
        );
    })
  );

// Epic to update the dummy payoff flag
const updateDummyPayoffVisibility$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(UPDATE_DUMMY_PAYOFF_VISIBILITY),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { userId } = action.payload;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      return ajax
        .put(
          `${config.apiUrl}/updateShowDummyDataPayoff`,
          { userId, show_dummy_data_payoff: false },
          headers
        )
        .pipe(
          map((response) => ({
            type: UPDATED_DUMMY_PAYOFF_VISIBILITY,
            payload: response.response,
          })),
          catchError((error: AjaxError) =>
            of({ type: ERROR_UPDATE_DUMMY_PAYOFF_VISIBILITY, error })
          )
        );
    })
  );

export const userEpic = combineEpics(
  addLocalStorageToState$,
  shouldFetchInfo$,
  fetchUserInfo$,
  contact$,
  updateDummyDataVisibility$,
  updateDummyPayoffVisibility$
);
